import { useMemo, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { HeaderCellBinding, TableField } from './types';
import { indexToColLetters } from './types';
import { cellRef, preambleColCount } from './headerCellsUtils';

interface HeaderCellsMapperProps {
  preambleRows: string[][];
  headerCells: HeaderCellBinding[];
  tableFields: TableField[];
  onUpsert: (binding: HeaderCellBinding) => void;
  onRemove: (cell: string) => void;
}

type EditorState = {
  cell: string;
  rawValue: string;
  asColumn: string;
  targetField: string;
  useSplit: boolean;
  splitPattern: string;
  part1Column: string;
  part1Field: string;
  part2Column: string;
  part2Field: string;
};

const defaultEditor = (cell: string, rawValue: string, existing?: HeaderCellBinding): EditorState => {
  const split = existing?.split;
  return {
    cell,
    rawValue,
    asColumn: existing?.asColumn || `Header ${cell}`,
    targetField: existing?.targetField || '',
    useSplit: Boolean(split?.parts?.length),
    splitPattern: split?.pattern || '^(\\d+)(?:\\s+(.+))?$',
    part1Column: split?.parts?.[0]?.asColumn || `Header ${cell} del 1`,
    part1Field: split?.parts?.[0]?.targetField || '',
    part2Column: split?.parts?.[1]?.asColumn || `Header ${cell} del 2`,
    part2Field: split?.parts?.[1]?.targetField || '',
  };
};

export const HeaderCellsMapper = ({
  preambleRows,
  headerCells,
  tableFields,
  onUpsert,
  onRemove,
}: HeaderCellsMapperProps) => {
  const { t } = useTranslation();
  const [editor, setEditor] = useState<EditorState | null>(null);

  const colCount = useMemo(() => preambleColCount(preambleRows), [preambleRows]);
  const bindingByCell = useMemo(() => {
    const map = new Map<string, HeaderCellBinding>();
    for (const b of headerCells) map.set(b.cell.toUpperCase(), b);
    return map;
  }, [headerCells]);

  const openCell = (rowIdx: number, colIdx: number) => {
    const cell = cellRef(rowIdx, colIdx);
    const rawValue = String(preambleRows[rowIdx]?.[colIdx] ?? '').trim();
    const existing = bindingByCell.get(cell);
    setEditor(defaultEditor(cell, rawValue, existing));
  };

  const saveEditor = () => {
    if (!editor || !editor.asColumn.trim()) return;
    const binding: HeaderCellBinding = {
      cell: editor.cell,
      asColumn: editor.asColumn.trim(),
      targetField: editor.useSplit ? undefined : (editor.targetField || undefined),
    };
    if (editor.useSplit) {
      binding.split = {
        pattern: editor.splitPattern || '^(\\d+)(?:\\s+(.+))?$',
        parts: [
          {
            group: 1,
            asColumn: editor.part1Column.trim() || `Header ${editor.cell} del 1`,
            targetField: editor.part1Field || undefined,
          },
          {
            group: 2,
            asColumn: editor.part2Column.trim() || `Header ${editor.cell} del 2`,
            targetField: editor.part2Field || undefined,
          },
        ],
      };
    }
    onUpsert(binding);
    setEditor(null);
  };

  if (!preambleRows.length) return null;

  const fieldOptions = tableFields.map((f) => (
    <option key={f.name} value={f.name}>{f.name}</option>
  ));

  return (
    <div className="bg-bgBase rounded-lg border border-border p-4 space-y-3" data-testid="header-cells-mapper">
      <div>
        <h4 className="font-medium text-textDefault">{t('imports.headerCellsTitle')}</h4>
        <p className="text-sm text-textSecondary mt-1">{t('imports.headerCellsHint')}</p>
      </div>

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-bgElevated">
              <th className="px-2 py-1.5 text-left text-textSecondary font-medium sticky left-0 bg-bgElevated">#</th>
              {Array.from({ length: colCount }, (_, i) => (
                <th key={i} className="px-2 py-1.5 text-left text-textSecondary font-medium min-w-[7rem]">
                  {indexToColLetters(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preambleRows.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-t border-border">
                <td className="px-2 py-1.5 text-textSecondary font-medium sticky left-0 bg-bgBase">{rowIdx + 1}</td>
                {Array.from({ length: colCount }, (_, colIdx) => {
                  const cell = cellRef(rowIdx, colIdx);
                  const value = String(row[colIdx] ?? '');
                  const bound = bindingByCell.get(cell);
                  return (
                    <td key={colIdx} className="px-1 py-1">
                      <button
                        type="button"
                        onClick={() => openCell(rowIdx, colIdx)}
                        title={bound ? `${bound.asColumn}${bound.targetField ? ` → ${bound.targetField}` : ''}` : t('imports.headerCellsClickToMap')}
                        className={`w-full text-left px-2 py-1.5 rounded border truncate max-w-[12rem] transition-colors ${
                          bound
                            ? 'border-primary bg-primary/10 text-textDefault'
                            : value.trim()
                              ? 'border-border bg-bgElevated text-textDefault hover:border-primary/50'
                              : 'border-transparent text-textTertiary hover:border-border'
                        }`}
                        data-testid={`header-cell-${cell}`}
                      >
                        {value.trim() || '·'}
                        {bound && (
                          <span className="block text-[10px] text-primary truncate mt-0.5">
                            {bound.split?.parts?.length
                              ? bound.split.parts.map((p) => p.targetField || p.asColumn).join(' + ')
                              : (bound.targetField || bound.asColumn)}
                          </span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {headerCells.length > 0 && (
        <ul className="text-sm space-y-1">
          {headerCells.map((b) => (
            <li key={b.cell} className="flex items-center justify-between gap-2 text-textSecondary">
              <span>
                <span className="font-medium text-textDefault">{b.cell}</span>
                {' → '}
                {b.split?.parts?.length
                  ? b.split.parts.map((p) => `${p.asColumn}${p.targetField ? ` (${p.targetField})` : ''}`).join(', ')
                  : `${b.asColumn}${b.targetField ? ` (${b.targetField})` : ''}`}
              </span>
              <button
                type="button"
                onClick={() => onRemove(b.cell)}
                className="text-xs text-red-500 hover:underline"
                data-testid={`header-cell-remove-${b.cell}`}
              >
                {t('imports.headerCellsRemove')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="header-cell-editor">
          <div className="bg-bgElevated border border-border rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h5 className="font-semibold text-textDefault">{t('imports.headerCellsEditorTitle')} {editor.cell}</h5>
                <p className="text-sm text-textSecondary mt-1 break-all">{editor.rawValue || t('imports.headerCellsEmpty')}</p>
              </div>
              <button type="button" onClick={() => setEditor(null)} className="text-textSecondary hover:text-textDefault">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.headerCellsAsColumn')}</label>
              <input
                type="text"
                value={editor.asColumn}
                onChange={(e) => setEditor({ ...editor, asColumn: e.target.value })}
                className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                data-testid="header-cell-as-column"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-textSecondary">
              <input
                type="checkbox"
                checked={editor.useSplit}
                onChange={(e) => setEditor({ ...editor, useSplit: e.target.checked })}
                className="rounded border-border"
                data-testid="header-cell-use-split"
              />
              {t('imports.headerCellsUseSplit')}
            </label>

            {!editor.useSplit ? (
              <div>
                <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.headerCellsTargetField')}</label>
                <select
                  value={editor.targetField}
                  onChange={(e) => setEditor({ ...editor, targetField: e.target.value })}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                  data-testid="header-cell-target-field"
                >
                  <option value="">{t('imports.headerCellsNoTarget')}</option>
                  {fieldOptions}
                </select>
              </div>
            ) : (
              <div className="space-y-3 border border-border rounded-lg p-3">
                <div>
                  <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.headerCellsSplitPattern')}</label>
                  <input
                    type="text"
                    value={editor.splitPattern}
                    onChange={(e) => setEditor({ ...editor, splitPattern: e.target.value })}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault font-mono text-xs"
                    data-testid="header-cell-split-pattern"
                  />
                  <p className="text-xs text-textSecondary mt-1">{t('imports.headerCellsSplitHint')}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-textSecondary mb-1">{t('imports.headerCellsPart1Column')}</label>
                    <input type="text" value={editor.part1Column}
                      onChange={(e) => setEditor({ ...editor, part1Column: e.target.value })}
                      className="w-full px-2 py-1.5 bg-bgBase border border-border rounded text-textDefault text-sm" />
                    <select value={editor.part1Field}
                      onChange={(e) => setEditor({ ...editor, part1Field: e.target.value })}
                      className="w-full mt-1 px-2 py-1.5 bg-bgBase border border-border rounded text-textDefault text-sm"
                      data-testid="header-cell-part1-field">
                      <option value="">{t('imports.headerCellsNoTarget')}</option>
                      {fieldOptions}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary mb-1">{t('imports.headerCellsPart2Column')}</label>
                    <input type="text" value={editor.part2Column}
                      onChange={(e) => setEditor({ ...editor, part2Column: e.target.value })}
                      className="w-full px-2 py-1.5 bg-bgBase border border-border rounded text-textDefault text-sm" />
                    <select value={editor.part2Field}
                      onChange={(e) => setEditor({ ...editor, part2Field: e.target.value })}
                      className="w-full mt-1 px-2 py-1.5 bg-bgBase border border-border rounded text-textDefault text-sm"
                      data-testid="header-cell-part2-field">
                      <option value="">{t('imports.headerCellsNoTarget')}</option>
                      {fieldOptions}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={() => { onRemove(editor.cell); setEditor(null); }}
                className="px-3 py-2 text-sm text-red-500 hover:underline"
                data-testid="header-cell-editor-remove"
              >
                {t('imports.headerCellsRemove')}
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditor(null)}
                  className="px-3 py-2 text-sm border border-border rounded-lg text-textSecondary hover:bg-bgBase">
                  {t('common.cancel') || 'Avbryt'}
                </button>
                <button type="button" onClick={saveEditor}
                  className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:opacity-90"
                  data-testid="header-cell-editor-save">
                  {t('common.save') || 'Spara'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
